import { jsonapiClient } from '@/api/client'
import type { components, paths } from '@/api/schema'
import type { ComputedRef } from 'vue'
import { useApiErrorHandler } from '@/composables/useApiErrorHandler'
import { findResource, isType, type Resource } from '@/api/resources'
import { usePagination, type PaginationOptions } from '@/composables/usePagination'
import { registerTypeIdentities } from '@/lib/typeIdentity'
import { jobContact, jobCompany } from '@/lib/customer'
import type { WonAttributes } from '@/lib/commitment'
import { toast } from 'vue-sonner'
import { useI18n } from 'vue-i18n'

type Kind = components['schemas']['Kind']
type Trade = components['schemas']['Trade']

// ⚠ THE ALLOWLIST IS THE WIRE. `update` spreads this straight into
// `attributes`, so a key missing from it never reaches the server — the PATCH
// goes out without it and toasts success. This list was hand-written and ate
// `cell_phone` on the contact twin for two days.
// Picked from the generated PATCH body so a key must EXIST in the contract to
// be named here. Status and the lifecycle events are deliberately out: they
// belong to updateStatus, which knows which transitions are legal.
type JobPatchAttributes = NonNullable<
  paths['/api/jobs/{id}']['patch']['requestBody']
>['content']['application/vnd.api+json']['data']['attributes']

type JobWritableAttrs = Pick<
  NonNullable<JobPatchAttributes>,
  'title' | 'description' | 'skip_proposal' | 'work_period_id' | 'season_year'
  | 'renewal_decline' | 'renewal_declined_forever' | 'underpriced' | 'payment_first'
>

// The POST body's attributes — the renewal's outcome and its 201 meta ride it.
type JobCreateAttributes = NonNullable<NonNullable<paths['/api/jobs']['post']['requestBody']>['content']['application/vnd.api+json']['data']['attributes']>
/** What the customer said on the renewal call (the desk's « À renouveler » block). */
export type RenewOutcome = NonNullable<JobCreateAttributes['renew_outcome']>
export type RenewClosingMeta = NonNullable<paths['/api/jobs']['post']['responses']['201']['content']['application/vnd.api+json']['meta']>

export type JobsQueryParams = paths['/api/jobs']['get']['parameters']['query']
export type JobFilter = components['parameters']['JobFilter']
export type JobStatus = NonNullable<JobFilter['status']>[number]

export function useJobs(
  queryParams?: ComputedRef<Partial<JobsQueryParams>>,
  options?: PaginationOptions
) {
  const { t } = useI18n()
  const { handleError } = useApiErrorHandler()

  const pagination = usePagination<Resource<'job'>, JobsQueryParams>(async (p) => {
    try {
      const response = await jsonapiClient.get('/api/jobs', { params: p })
      const included = response.data.included || []
      // A job list that sideloads its types feeds the identity registry, so every
      // TypeMark it draws has the chosen color and icon on FIRST paint. Without
      // this the marks wait on some other surface having loaded the templates —
      // which on a page that shows only jobs never happens, so the types render
      // as hash-coloured dots with no icon until something else fetches them.
      registerTypeIdentities(included.filter((r): r is Resource<'job_template'> => isType(r, 'job_template')))
      return {
        data: response.data.data,
        included,
        meta: response.data.meta
      }
    } catch (error) {
      handleError(error)
      return null
    }
  }, queryParams, options)

  const getServiceLocation = (job: Resource<'job'>): Resource<'service_location'> | null => {
    const slId = job.relationships?.service_location?.data?.id
    return findResource(pagination.included.value, slId, 'service_location')
  }

  // The job's OWN customer (the frozen stamp) — see lib/customer. Requires
  // `include: { contact: true, company: true }`.
  const getContact = (job: Resource<'job'>): Resource<'contact'> | null =>
    jobContact(job, pagination.included.value)

  const getCompany = (job: Resource<'job'>): Resource<'company'> | null =>
    jobCompany(job, pagination.included.value)

  // The job's type (JobTemplate) from the included set, or null when untyped.
  const getJobTemplate = (job: Resource<'job'>): Resource<'job_template'> | null =>
    findResource(pagination.included.value, job.relationships?.job_template?.data?.id, 'job_template')

  // A job's visits from the included set (sorted by position).
  const getVisits = (job: Resource<'job'>): Resource<'visit'>[] => {
    const relData = job.relationships?.visits?.data
    if (!Array.isArray(relData) || !relData.length) return []
    const idSet = new Set(relData.map(v => v?.id).filter((id): id is string => !!id))
    return pagination.included.value
      .filter((r): r is Resource<'visit'> => isType(r, 'visit') && idSet.has(r.id))
      .sort((a, b) => (a.attributes?.position ?? 0) - (b.attributes?.position ?? 0))
  }

  const create = async (data: {
    service_location_id: string;
    squad_id?: string;
    title: string;
    description?: string;
    scheduled_at?: string;
    duration_minutes?: number;
    skip_proposal?: boolean;
    // A SCOPED LEAD (« travaux connus, soumission à envoyer ») — creates NO
    // visits and is born assessed. ⚠ Every attribute this create() sends must be
    // named here AND spread into the body below: the builder is an allowlist,
    // and a caller spreading an un-listed key (`...{ work_scoped: true }`) slips
    // past TS's excess-property check and is silently dropped (the bug this
    // comment guards against — the scoped lead created a visit anyway).
    work_scoped?: boolean;
    // A CAPTURED LEAD (the three-field « Nouveau lead »): creates NO visits and
    // stays open — the route out (évaluation or soumission) is a later verb.
    // Distinct from work_scoped, which is born ASSESSED (scope already taken).
    lead_capture?: boolean;
    // THE MONEY ANSWER at intake, both priced spellings (commitmentAttributes'
    // derivation — the same pair markWonVerbal sends): the custom accepted
    // one-liner, or the type's template at the entered numbers. Either mints
    // the job's accepted soumission at birth and the acceptance chain approves
    // it. Exclusive with skip_proposal (server 422s the pair).
    // ⚠ These are the names the SERVER reads. Their pre-template ancestors
    // (won_amount/won_label) outlived the controller that read them, and since
    // callers SPREAD the win object the excess-property check never fired: the
    // money answer was dropped here, so a priced intake created its visits, no
    // paper and no approval, and the deal sat at `open` wearing a lead's face.
    won_line?: WonAttributes['won_line'];
    won_template?: WonAttributes['won_template'];
    assignees?: { userId: string, kind: Kind, trade: Trade }[];
    // Create-from-template: one element per visit to spawn (overrides the
    // single-visit seed) + the template's provenance id.
    visits?: {
      name?: string | null;
      // work (a treatment — the default) or evaluation (the sales look-see).
      kind?: 'work' | 'evaluation';
      scheduled_at?: string | null;
      duration_minutes?: number | null;
      interval_min_days?: number | null;
      interval_max_days?: number | null;
      flexible?: boolean;
      // « Fenêtre flexible » booking: a manual window on visit 1 with NO
      // scheduled_at. window_overridden is what makes it survive the post-create
      // recompute (which otherwise clears visit 1's window); the visit then sits
      // in the bank until the sector fill places it.
      due_from?: string | null;
      due_to?: string | null;
      window_overridden?: boolean;
      // « Entente prise » at intake — the customer agreed on the call that the
      // visit will happen (no date needed): the look-see lands in the Banque
      // évaluations instead of « À contacter ».
      confirmed?: boolean;
      assignees?: { user_id: string, kind: Kind, trade: Trade }[];
    }[];
    job_template_id?: string;
    // The chain: the job this one is opened FROM (« Nouveau job ici »,
    // « Nouveau job lié »). Read-only provenance; born assessed server-side
    // only when the parent's look-see is already done.
    origin_job_id?: string;
    // THE SEASON THE JOB IS FOR — one of the type's seasons and a year
    // (« Taille 2027 »); the run is born idle on demand. Both or neither.
    // Absent, the server applies the type's default — the same answer the
    // intake line showed as its prefill.
    work_period_id?: string;
    season_year?: number;
  // `silent`: the caller owns a richer confirmation (the board's lead toast
  // carries a « Planifier l'évaluation » action) — two stacked toasts is noise.
  }, options?: { successKey?: string; silent?: boolean }) => {
    try {
      const response = await jsonapiClient.post('/api/jobs', {
        data: {
          type: 'job' as const,
          attributes: {
            title: data.title,
            description: data.description || null,
            skip_proposal: data.skip_proposal ?? false,
            ...(data.work_scoped ? { work_scoped: true } : {}),
            ...(data.lead_capture ? { lead_capture: true } : {}),
            ...(data.won_line ? { won_line: data.won_line } : {}),
            ...(data.won_template ? { won_template: data.won_template } : {}),
            ...(data.work_period_id && data.season_year
              ? { work_period_id: data.work_period_id, season_year: data.season_year }
              : {}),
            // writeOnly — the backend seeds the first visit from these.
            scheduled_at: data.scheduled_at,
            duration_minutes: data.duration_minutes,
            ...(data.visits && data.visits.length > 0 ? { visits: data.visits } : {}),
          },
          relationships: {
            service_location: {
              data: {
                type: 'service_location' as const,
                id: data.service_location_id
              }
            },
            ...(data.squad_id ? {
              squad: {
                data: { type: 'squad' as const, id: data.squad_id }
              }
            } : {}),
            ...(data.job_template_id ? {
              job_template: {
                data: { type: 'job_template' as const, id: data.job_template_id }
              }
            } : {}),
            ...(data.origin_job_id ? {
              origin_job: {
                data: { type: 'job' as const, id: data.origin_job_id }
              }
            } : {}),
            ...(data.assignees && data.assignees.length > 0 ? {
              visit_assignees: {
                data: data.assignees.map(a => ({
                  type: 'user' as const,
                  id: a.userId,
                  meta: {
                    kind: a.kind,
                    trade: a.trade
                  }
                }))
              }
            } : {})
          }
        }
      })
      const newJob = response.data.data
      pagination.prependItem(newJob)
      if (!options?.silent) toast.success(t(options?.successKey ?? 'jobs.createSuccess'))
      return newJob
    } catch (error) {
      handleError(error)
      return null
    }
  }

  // skip_proposal: false is the UN-SKIP (« Finalement, une soumission ») —
  // the flag alone, never a status event: the pipeline re-derives from
  // documents once the paper exists.
  // opts.silent: a caller folding the update into its own act (the work
  // door's note write-back) keeps its own toast — one act, one toast.
  const update = async (id: string, attrs: JobWritableAttrs, opts: { silent?: boolean } = {}) => {
    try {
      const response = await jsonapiClient.patch('/api/jobs/{id}',
        { data: { id, type: 'job' as const, attributes: attrs } },
        { path: { id } }
      )
      const updatedJob = response.data.data
      pagination.replaceItem(id, updatedJob)
      if (!opts.silent) toast.success(t('jobs.updateSuccess'))
      return updatedJob
    } catch (error) {
      handleError(error)
      return null
    }
  }

  // « RENOUVELER POUR TAILLE 2027 » — a renewal is a SALE. One POST with the
  // writeOnly `renew_from_job_id`: the parent supplies customer, address, type
  // and last year's deal (copied as a draft); the child is born assessed,
  // chained (`origin_job`), on the named season (the run born on demand) — or,
  // for a year-round type, with one work visit floored at `notBefore`.
  // Returns the CHILD; the caller lands on it.
  const renew = async (jobId: string, opts: { workPeriodId?: string; seasonYear?: number; notBefore?: string | null } = {}) => {
    try {
      const response = await jsonapiClient.post('/api/jobs', {
        data: {
          type: 'job' as const,
          attributes: {
            renew_from_job_id: jobId,
            ...(opts.workPeriodId && opts.seasonYear ? { work_period_id: opts.workPeriodId, season_year: opts.seasonYear } : {}),
            ...(opts.notBefore ? { not_before: opts.notBefore } : {}),
          },
        },
      })
      const child = response.data.data
      const label = child.attributes.season_label
      toast.success(label ? t('jobs.renew.toast', { label }) : t('jobs.renew.toastPlain'))
      return child
    } catch (error) {
      handleError(error)
      return null
    }
  }

  // ★ THE CALL CLOSES THE FILE — the desk's « À renouveler » block: the same
  // POST with `renew_outcome` (what the customer said) and, for « Vendu », the
  // amount as a `won_line`. SILENT on purpose: the door toasts in the
  // outcome's own words from the 201's meta (whether the copy went out, and
  // by mail). Returns the child and that meta.
  const renewClosing = async (
    jobId: string,
    opts: { workPeriodId?: string; seasonYear?: number; outcome: RenewOutcome; wonLine?: WonAttributes['won_line'] },
  ) => {
    try {
      const response = await jsonapiClient.post('/api/jobs', {
        data: {
          type: 'job' as const,
          attributes: {
            renew_from_job_id: jobId,
            renew_outcome: opts.outcome,
            ...(opts.workPeriodId && opts.seasonYear ? { work_period_id: opts.workPeriodId, season_year: opts.seasonYear } : {}),
            ...(opts.wonLine ? { won_line: opts.wonLine } : {}),
          },
        },
      })
      return { job: response.data.data, meta: response.data.meta ?? null }
    } catch (error) {
      handleError(error)
      return null
    }
  }

  // ── The customer link's console verbs (doc/FEATURE_PUBLIC_PROPOSAL.md) ────
  // writeOnly keys on the ordinary PATCH, per house convention. They live on
  // the JOB because the link does: ONE URL per deal, listing its pending
  // soumissions. The 422s carry their stated reason (mail unconfigured / no
  // email / not presented) — the standard handler toasts it, and the degrade
  // path is always « copier le lien ».
  const patchPublicLink = async (
    id: string,
    attributes: { regenerate_public_link: true } | { send_public_link_email: true },
  ) => {
    try {
      const response = await jsonapiClient.patch('/api/jobs/{id}',
        { data: { id, type: 'job' as const, attributes } },
        { path: { id } }
      )
      const updatedJob = response.data.data
      pagination.replaceItem(id, updatedJob)
      return updatedJob
    } catch (error) {
      handleError(error)
      return null
    }
  }

  // « Régénérer le lien » — kills every previously shared URL of this deal.
  const regeneratePublicLink = (id: string) => patchPublicLink(id, { regenerate_public_link: true })
  // « Envoyer par courriel » — mails the link and its soumissions to the contact.
  const sendPublicLinkEmail = (id: string) => patchPublicLink(id, { send_public_link_email: true })

  const updateStatus = async (id: string, statusOrEvent: string, isEvent = false) => {
    try {
      const payload = isEvent ? { event: statusOrEvent } : { status: statusOrEvent }
      const response = await jsonapiClient.patch('/api/jobs/{id}',
        {
          data: {
            id,
            type: 'job' as const,
            attributes: payload
          }
        },
        { path: { id } }
      )
      const updatedJob = response.data.data
      pagination.replaceItem(id, updatedJob)
      toast.success(t('jobs.statusUpdated'))
      return updatedJob
    } catch (error) {
      handleError(error)
      return null
    }
  }

  // The VERBAL WIN — the customer said go. ONE PATCH, three honest spellings
  // (won = commitmentAttributes' derivation, the same one intake uses): the
  // type's TEMPLATE at the entered numbers, the custom AGREED LINE, or the
  // explicit « Sans soumission » (skip_proposal + approve — the acceptance
  // chain fires :approve for the priced pair, so no event rides beside them).
  // All three leave the lead phase as committed work, same job, provenance
  // intact.
  const markWonVerbal = async (id: string, won: WonAttributes) => {
    try {
      const response = await jsonapiClient.patch('/api/jobs/{id}',
        {
          data: {
            id,
            type: 'job' as const,
            attributes: won.skip_proposal
              ? { skip_proposal: true, event: 'approve' }
              : {
                  ...(won.won_line ? { won_line: won.won_line } : {}),
                  ...(won.won_template ? { won_template: won.won_template } : {}),
                },
          },
        },
        { path: { id } }
      )
      const updatedJob = response.data.data
      pagination.replaceItem(id, updatedJob)
      toast.success(t('pipeline.card.wonVerbalToast'))
      return updatedJob
    } catch (error) {
      handleError(error)
      return null
    }
  }

  const remove = async (id: string) => {
    try {
      await jsonapiClient.delete('/api/jobs/{id}', {
        path: { id }
      })
      pagination.removeItem(id)
      toast.success(t('jobs.deleteSuccess'))
      return true
    } catch (error) {
      handleError(error)
      return false
    }
  }

  return {
    jobs: pagination.data,
    included: pagination.included,
    loading: pagination.loading,
    ready: pagination.ready,
    meta: pagination.meta,
    hasMore: pagination.hasMore,
    failed: pagination.failed,
    fetchJobs: pagination.fetchPage,
    refetchJobs: pagination.refetch,
    loadMoreJobs: pagination.loadMore,
    getServiceLocation,
    getContact,
    getCompany,
    getJobTemplate,
    getVisits,
    create,
    update,
    updateStatus,
    regeneratePublicLink,
    sendPublicLinkEmail,
    markWonVerbal,
    renew,
    renewClosing,
    remove
  }
}
